"use client";

import React from "react";
import Navbar from "../../components/account/Navbar";
import "../../styles/clinicsetup.css";
import { useRef, useState, useEffect, useMemo } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import ClinicAdditionModal from "../../components/modals/Clinicadditionmodal";
import { resetClinicData, useClinicData } from "../../hooks/useClinicData";
import { showErrorToast } from "../../utils/notificationUtils";
import api from "../../services/api";
import TimePicker from "react-time-picker";
import "react-time-picker/dist/TimePicker.css";
import "react-clock/dist/Clock.css";
import BasicInfoSection from "./setup-components/BasicInfoSection";
import BusinessDetailsSection from "./setup-components/BusinessDetailsSection";
import ServicesSection from "./setup-components/ServicesSection";
import LocationSection from "./setup-components/LocationSection";
import { ResolutionGuard } from "../../hooks/useScreenResolution.jsx";

const API_BASE_URL = import.meta.env.VITE_API_URL || "http://localhost:8000";
const STEP_KEYS = ["basicInfo", "businessDetails", "location", "categories"];

const EditClinic = ({ clinicData, clinic_id }) => {
  const navigate = useNavigate();
  const { t, i18n } = useTranslation();
  const { refresh } = useClinicData();
  const stepLabels = useMemo(
    () => STEP_KEYS.map((key) => t(`clinicSetup.steps.${key}`)),
    [t, i18n.language]
  );

  // Get clinic ID from prop or clinicData
  const clinicIdValue = clinic_id || clinicData?.clinic?.id;

  // Require clinic ID for editing
  if (!clinicIdValue) {
    return (
      <div
        className="clinicsetup-page-wrapper"
        dir={i18n.dir()}
        lang={i18n.language}
      >
        <Navbar />
        <div className="clinicsetup-container">
          <div style={{ padding: "40px", textAlign: "center" }}>
            <p style={{ color: "#e53935", fontSize: "18px" }}>
              {t("clinicSetup.errors.missingClinic")}
            </p>
            <button
              onClick={() => {
                const targetClinicId = clinicIdValue || clinicData?.clinic?.id;
                if (targetClinicId) {
                  navigate(`/medical/clinic/${targetClinicId}/setup`);
                } else {
                  navigate("/medical/clinic/setup");
                }
              }}
              style={{
                marginTop: "20px",
                padding: "10px 20px",
                backgroundColor: "#3b82f6",
                color: "white",
                border: "none",
                borderRadius: "6px",
                cursor: "pointer",
              }}
            >
              {t("clinicSetup.buttons.goToSetup")}
            </button>
          </div>
        </div>
      </div>
    );
  }
  const [currentSection, setCurrentSection] = useState(0);
  const LAST_SECTION_INDEX = 3;
  const fileInputRef = useRef(null);
  const dateInputRef = useRef(null);
  const formScrollContainerRef = useRef(null);
  const stepNavigationRef = useRef(null);

  // Scroll to top when section changes
  useEffect(() => {
    // Use setTimeout to ensure DOM has updated with new section content
    const scrollToTop = setTimeout(() => {
      // Try multiple scroll methods to ensure it works
      // 1. Scroll the form container directly
      if (formScrollContainerRef.current) {
        formScrollContainerRef.current.scrollTop = 0;
      }

      // 2. Scroll step navigation into view (this works on any scrollable ancestor)
      if (stepNavigationRef.current) {
        stepNavigationRef.current.scrollIntoView({
          behavior: 'smooth',
          block: 'start',
          inline: 'nearest'
        });
      }

      // 3. Scroll window/document as fallback
      document.documentElement.scrollTo({ top: 0, behavior: 'smooth' });
      document.body.scrollTo({ top: 0, behavior: 'smooth' });
    }, 200);

    return () => clearTimeout(scrollToTop);
  }, [currentSection]);
  const [errors, setErrors] = useState([]);
  const [error, setError] = useState("");
  const [showClinicAdditionModal, setShowClinicAdditionModal] = useState(false);
  const [countryOptions, setCountryOptions] = useState([]); // For react-select
  const [countries, setCountries] = useState([]);
  const [cityOptions, setCityOptions] = useState([]);
  const [clinicTypeOptions, setClinicTypeOptions] = useState([]);

  const token = localStorage.getItem("access_token");
  const [logoPreview, setLogoPreview] = useState(null);
  const [submitError, setSubmitError] = useState(""); // For submit error
  const [isLoading, setIsLoading] = useState(false); // For loading state

  // Removed undefined clinicId reference
  // All form data in one state object
  const [form, setForm] = useState({
    // Basic Information
    logo: null,
    name_en: "",
    name_ar: "",
    establishmentDate: "",
    type: "",
    // Services
    services: "",
    registrationNumber: "",
    vatNumber: "",
    currency: "",
    invoiceStart: "1",
    servicesOffered: [],
    // Contact
    email: "",
    telephone: "",
    mobile: "",
    website: "",
    country: "",
    city: "",
    street: "",
    buildingNumber: "",
    floorNumber: "",
    map_coordinates: {},
    latitude: "",
    longitude: "",
    mapSearchQuery: "",
    workingHours: {
      sunday: { enabled: false, from: "", to: "" },
      monday: { enabled: false, from: "", to: "" },
      tuesday: { enabled: false, from: "", to: "" },
      wednesday: { enabled: false, from: "", to: "" },
      thursday: { enabled: false, from: "", to: "" },
      friday: { enabled: false, from: "", to: "" },
      saturday: { enabled: false, from: "", to: "" },
    },
    is_pet_store: false,
  });

  // Clear section-specific errors when navigating away from a section
  useEffect(() => {
    setErrors((prev) => {
      const newErrors = { ...prev };
      // Clear errors for sections we're not currently on
      if (currentSection !== 1) {
        delete newErrors.workingHours;
        delete newErrors.registrationNumber;
        delete newErrors.vatNumber;
        delete newErrors.currency;
        delete newErrors.invoiceStart;
      }
      if (currentSection !== 0) {
        delete newErrors.name_en;
        delete newErrors.name_ar;
        delete newErrors.type;
        delete newErrors.email;
        delete newErrors.mobile;
      }
      if (currentSection !== 2) {
        delete newErrors.country;
        delete newErrors.city;
        delete newErrors.street;
        delete newErrors.buildingNumber;
        delete newErrors.mapLocation;
      }
      if (currentSection !== 3) {
        delete newErrors.servicesOffered;
      }
      return newErrors;
    });
  }, [currentSection]);

  // Clear workingHours error when at least one day is enabled
  useEffect(() => {
    const hasEnabledDay = Object.values(form.workingHours || {}).some((day) => day?.enabled);
    if (hasEnabledDay && errors.workingHours) {
      setErrors((prev) => {
        const newErrors = { ...prev };
        delete newErrors.workingHours;
        return newErrors;
      });
    }
  }, [
    form.workingHours?.sunday?.enabled,
    form.workingHours?.monday?.enabled,
    form.workingHours?.tuesday?.enabled,
    form.workingHours?.wednesday?.enabled,
    form.workingHours?.thursday?.enabled,
    form.workingHours?.friday?.enabled,
    form.workingHours?.saturday?.enabled,
    errors.workingHours
  ]);

  function isSectionValid(sectionIdx) {
    switch (sectionIdx) {
      case 0: // Basic Info
        return (
          form.name_en && form.name_ar && form.type && form.email && form.mobile
        );
      case 1: // Business Details
        return (
          form.registrationNumber &&
          form.vatNumber &&
          form.currency &&
          form.invoiceStart &&
          Object.values(form.workingHours).some((day) => day.enabled)
        );
      case 2: // Location
        return (
          form.country &&
          form.city &&
          form.street &&
          form.buildingNumber &&
          form.latitude &&
          form.longitude
        );

      case 3: // Services
        return form.servicesOffered.length > 0;

      default:
        return true;
    }
  }

  const validateCurrentSection = () => {
    const newErrors = {};

    switch (currentSection) {
      case 0:
        if (!form.name_en) newErrors.name_en = t("clinicSetup.validation.nameEnRequired");
        if (!form.name_ar) newErrors.name_ar = t("clinicSetup.validation.nameArRequired");
        if (!form.type) newErrors.type = t("clinicSetup.validation.typeRequired");
        if (!form.email) newErrors.email = t("clinicSetup.validation.emailRequired");
        if (!form.mobile) newErrors.mobile = t("clinicSetup.validation.mobileRequired");
        break;
      case 1:
        if (!form.registrationNumber)
          newErrors.registrationNumber = t("clinicSetup.validation.registrationNumberRequired");
        if (!form.vatNumber) newErrors.vatNumber = t("clinicSetup.validation.vatNumberRequired");
        if (!form.currency) newErrors.currency = t("clinicSetup.validation.currencyRequired");
        if (!form.invoiceStart) newErrors.invoiceStart = t("clinicSetup.validation.invoiceStartRequired");
        if (!Object.values(form.workingHours).some((day) => day.enabled)) {
          newErrors.workingHours = t("clinicSetup.validation.workingHoursRequired");
        }
        break;
      case 2:
        if (!form.country) newErrors.country = t("clinicSetup.validation.countryRequired");
        if (!form.city) newErrors.city = t("clinicSetup.validation.cityRequired");
        if (!form.street) newErrors.street = t("clinicSetup.validation.streetRequired");
        if (!form.buildingNumber)
          newErrors.buildingNumber = t("clinicSetup.validation.buildingNumberRequired");
        break;
      case 3:
        if (form.servicesOffered.length === 0) {
          newErrors.servicesOffered = t("clinicSetup.validation.servicesRequired");
        }
        break;
      default:
        break;
    }

    setErrors(newErrors);

    if (Object.keys(newErrors).length > 0) {
      showErrorToast(t("clinicSetup.alerts.requiredFields"));
      return false;
    }

    return true;
  };

  const validateAllSections = () => {
    const newErrors = {};

    // Helper function to check if a value is empty (handles strings, arrays, etc.)
    const isEmpty = (value) => {
      if (value === null || value === undefined) return true;
      if (typeof value === 'string') return value.trim() === '';
      if (Array.isArray(value)) return value.length === 0;
      return !value;
    };

    // Section 0: Basic Info
    if (isEmpty(form.name_en)) newErrors.name_en = t("clinicSetup.validation.nameEnRequired");
    if (isEmpty(form.name_ar)) newErrors.name_ar = t("clinicSetup.validation.nameArRequired");
    if (isEmpty(form.type)) newErrors.type = t("clinicSetup.validation.typeRequired");
    if (isEmpty(form.email)) newErrors.email = t("clinicSetup.validation.emailRequired");
    if (isEmpty(form.mobile)) newErrors.mobile = t("clinicSetup.validation.mobileRequired");

    // Section 1: Business Details
    if (isEmpty(form.registrationNumber))
      newErrors.registrationNumber = t("clinicSetup.validation.registrationNumberRequired");
    if (isEmpty(form.vatNumber)) newErrors.vatNumber = t("clinicSetup.validation.vatNumberRequired");
    if (isEmpty(form.currency)) newErrors.currency = t("clinicSetup.validation.currencyRequired");
    if (isEmpty(form.invoiceStart)) newErrors.invoiceStart = t("clinicSetup.validation.invoiceStartRequired");
    if (!Object.values(form.workingHours || {}).some((day) => day?.enabled)) {
      newErrors.workingHours = t("clinicSetup.validation.workingHoursRequired");
    }

    // Section 2: Location
    if (isEmpty(form.country)) newErrors.country = t("clinicSetup.validation.countryRequired");
    if (isEmpty(form.city)) newErrors.city = t("clinicSetup.validation.cityRequired");
    if (isEmpty(form.street)) newErrors.street = t("clinicSetup.validation.streetRequired");
    if (isEmpty(form.buildingNumber))
      newErrors.buildingNumber = t("clinicSetup.validation.buildingNumberRequired");
    if (isEmpty(form.latitude) || isEmpty(form.longitude)) {
      newErrors.mapLocation = t("clinicSetup.validation.mapLocationRequired");
    }

    // Section 3: Services
    if (!form.servicesOffered || form.servicesOffered.length === 0) {
      newErrors.servicesOffered = t("clinicSetup.validation.servicesRequired");
    }

    setErrors(newErrors);

    if (Object.keys(newErrors).length > 0) {
      showErrorToast(t("clinicSetup.alerts.requiredFields"));
      return false;
    }

    return true;
  };

  useEffect(() => {
    // Use centralized data if available
    if (clinicData?.clinic_types) {
      setClinicTypeOptions(
        clinicData.clinic_types.map((type) => ({
          value: type.id,
          label: type.name_en,
        }))
      );
    } else {
      // Fallback to API call if centralized data not available
      const fetchClinicTypes = async () => {
        try {
          const response = await fetch(
            `${API_BASE_URL}/api/v1/clinic-types/active`
          );
          if (!response.ok) throw new Error("Failed to fetch clinic types");
          const result = await response.json();
          if (Array.isArray(result.data)) {
            setClinicTypeOptions(
              result.data.map((type) => ({
                value: type.id,
                label: type.name_en,
              }))
            );
          }
        } catch (err) {
          console.error("Error fetching clinic types:", err);
        }
      };
      fetchClinicTypes();
    }

    // Load clinic data for editing
    setIsLoading(true);
    if (clinicIdValue) {

      // Use centralized data if available
      if (clinicData?.clinic) {
        const clinic = clinicData.clinic;
        // Store clinic object in localStorage for reception page
        if (clinicIdValue) {
          localStorage.setItem(
            "clinic",
            JSON.stringify({ is_owner: clinic.is_owner, clinic_id: clinicIdValue })
          );
        }
        // Map service_lists to servicesOffered for checked boxes
        const servicesOffered = Array.isArray(clinic.service_lists)
          ? clinic.service_lists.map((s) => s.id)
          : [];

        // Transform working_hours array to object structure
        const transformedWorkingHours = {
          sunday: { enabled: false, from: "", to: "" },
          monday: { enabled: false, from: "", to: "" },
          tuesday: { enabled: false, from: "", to: "" },
          wednesday: { enabled: false, from: "", to: "" },
          thursday: { enabled: false, from: "", to: "" },
          friday: { enabled: false, from: "", to: "" },
          saturday: { enabled: false, from: "", to: "" },
        };

        if (Array.isArray(clinic.working_hours)) {
          clinic.working_hours.forEach((wh) => {
            // Map day name to lowercase format (handle both lowercase and uppercase from API)
            let dayKey = wh.day;
            if (dayKey) {
              // Convert to lowercase and handle various formats
              const dayMap = {
                'sun': 'sunday',
                'mon': 'monday',
                'tue': 'tuesday',
                'wed': 'wednesday',
                'thu': 'thursday',
                'fri': 'friday',
                'sat': 'saturday',
                'sunday': 'sunday',
                'monday': 'monday',
                'tuesday': 'tuesday',
                'wednesday': 'wednesday',
                'thursday': 'thursday',
                'friday': 'friday',
                'saturday': 'saturday'
              };
              dayKey = dayMap[dayKey.toLowerCase()] || dayKey.toLowerCase();

              if (transformedWorkingHours[dayKey]) {
                transformedWorkingHours[dayKey] = {
                  enabled: true, // If day exists in working_hours, it's enabled
                  from: wh.opening_time || "",
                  to: wh.closing_time || "",
                };
              }
            }
          });
        }

        setForm((prev) => {
          // Fallback logic for latitude/longitude
          let latitude = "";
          let longitude = "";
          if (
            clinic.map_coordinates &&
            clinic.map_coordinates.lat &&
            clinic.map_coordinates.lng
          ) {
            latitude = clinic.map_coordinates.lat.toString();
            longitude = clinic.map_coordinates.lng.toString();
          } else if (clinic.latitude && clinic.longitude) {
            latitude = clinic.latitude.toString();
            longitude = clinic.longitude.toString();
          }
          return {
            ...prev,
            name_en: clinic.name_en || "",
            name_ar: clinic.name_ar || "",
            establishmentDate: clinic.establishment_date || "",
            type: clinic.type || "",
            registrationNumber: clinic.registration_number || "",
            vatNumber: clinic.vat_number || "",
            currency: clinic.currency || "",
            invoiceStart: clinic.invoice_starting_number || "",
            email: clinic.email || "",
            telephone: clinic.telephone || "",
            mobile: clinic.mobile || "",
            website: clinic.website || "",
            country:
              (clinic.country_id ??
                (typeof clinic.country === "object"
                  ? clinic.country?.id
                  : clinic.country)) ?? "",
            city:
              (clinic.city_id ??
                (typeof clinic.city === "object" ? clinic.city?.id : clinic.city)) ??
              "",
            street: clinic.full_address || "",
            buildingNumber: clinic.building_number || "",
            floorNumber: clinic.floor_number || "",
            map_coordinates: clinic.map_coordinates || {},
            latitude: latitude,
            longitude: longitude,
            mapSearchQuery: "",
            servicesOffered,
            workingHours: transformedWorkingHours,
            is_pet_store:
              Boolean(clinic.is_pet_store) &&
              (clinic.is_pet_store === 1 ||
                clinic.is_pet_store === true ||
                clinic.is_pet_store === "1"),
            // Add other fields as needed
            logo: null, // Logo file can't be pre-filled
          };
        });
        if (clinic.logo) {
          setLogoPreview(clinic.logo);
        }
        setIsLoading(false);
      } else {
        // Fallback to API call if centralized data not available
        fetch(`${API_BASE_URL}/api/v1/clinics/${clinicIdValue}`, {
          headers: {
            Authorization: `Bearer ${token}`,
            Accept: "application/json",
          },
        })
          .then((res) => res.json())
          .then((result) => {
            if (result.status === "success" && result.data) {
              const clinic = result.data;
              // Map service_lists to servicesOffered for checked boxes
              const servicesOffered = Array.isArray(clinic.service_lists)
                ? clinic.service_lists.map((s) => s.id)
                : [];

              // Transform working_hours array to object structure
              const transformedWorkingHours = {
                sunday: { enabled: false, from: "", to: "" },
                monday: { enabled: false, from: "", to: "" },
                tuesday: { enabled: false, from: "", to: "" },
                wednesday: { enabled: false, from: "", to: "" },
                thursday: { enabled: false, from: "", to: "" },
                friday: { enabled: false, from: "", to: "" },
                saturday: { enabled: false, from: "", to: "" },
              };

              if (Array.isArray(clinic.working_hours)) {
                clinic.working_hours.forEach((wh) => {
                  // Map day name to lowercase format (handle both lowercase and uppercase from API)
                  let dayKey = wh.day;
                  if (dayKey) {
                    // Convert to lowercase and handle various formats
                    const dayMap = {
                      'sun': 'sunday',
                      'mon': 'monday',
                      'tue': 'tuesday',
                      'wed': 'wednesday',
                      'thu': 'thursday',
                      'fri': 'friday',
                      'sat': 'saturday',
                      'sunday': 'sunday',
                      'monday': 'monday',
                      'tuesday': 'tuesday',
                      'wednesday': 'wednesday',
                      'thursday': 'thursday',
                      'friday': 'friday',
                      'saturday': 'saturday'
                    };
                    dayKey = dayMap[dayKey.toLowerCase()] || dayKey.toLowerCase();

                    if (transformedWorkingHours[dayKey]) {
                      transformedWorkingHours[dayKey] = {
                        enabled: true, // If day exists in working_hours, it's enabled
                        from: wh.opening_time || "",
                        to: wh.closing_time || "",
                      };
                    }
                  }
                });
              }

              setForm((prev) => {
                // Fallback logic for latitude/longitude
                let latitude = "";
                let longitude = "";
                if (
                  clinic.map_coordinates &&
                  clinic.map_coordinates.lat &&
                  clinic.map_coordinates.lng
                ) {
                  latitude = clinic.map_coordinates.lat.toString();
                  longitude = clinic.map_coordinates.lng.toString();
                } else if (clinic.latitude && clinic.longitude) {
                  latitude = clinic.latitude.toString();
                  longitude = clinic.longitude.toString();
                }
                return {
                  ...prev,
                  name_en: clinic.name_en || "",
                  name_ar: clinic.name_ar || "",
                  establishmentDate: clinic.establishment_date || "",
                  type: clinic.type || "",
                  registrationNumber: clinic.registration_number || "",
                  vatNumber: clinic.vat_number || "",
                  currency: clinic.currency || "",
                  invoiceStart: clinic.invoice_starting_number || "",
                  email: clinic.email || "",
                  telephone: clinic.telephone || "",
                  mobile: clinic.mobile || "",
                  website: clinic.website || "",
                  country:
                    (clinic.country_id ??
                      (typeof clinic.country === "object"
                        ? clinic.country?.id
                        : clinic.country)) ?? "",
                  city:
                    (clinic.city_id ??
                      (typeof clinic.city === "object"
                        ? clinic.city?.id
                        : clinic.city)) ?? "",
                  street: clinic.full_address || "",
                  buildingNumber: clinic.building_number || "",
                  floorNumber: clinic.floor_number || "",
                  map_coordinates: clinic.map_coordinates || {},
                  latitude: latitude,
                  longitude: longitude,
                  mapSearchQuery: "",
                  servicesOffered,
                  workingHours: transformedWorkingHours,
                  is_pet_store:
                    Boolean(clinic.is_pet_store) &&
                    (clinic.is_pet_store === 1 ||
                      clinic.is_pet_store === true ||
                      clinic.is_pet_store === "1"),
                  // Add other fields as needed
                  logo: null, // Logo file can't be pre-filled
                };
              });
              if (clinic.logo) {
                setLogoPreview(clinic.logo);
              }
            }
            setIsLoading(false);
          })
          .catch((err) => {
            console.error("Error fetching clinic data:", err);
            setIsLoading(false);
          });
      }
    } else {
      setIsLoading(false);
      setSubmitError(t("clinicSetup.errors.missingClinic"));
    }
  }, [clinicIdValue, clinicData, token]);

  // Load Google Maps API and initialize interactive map
  useEffect(() => {
    const loadGoogleMaps = () => {
      if (window.google && window.google.maps) {
        initializeInteractiveMap();
        return;
      }

      const script = document.createElement("script");
      script.src = `https://maps.googleapis.com/maps/api/js?key=${import.meta.env.VITE_GOOGLE_MAP_API_KEY ||
        "AIzaSyBFw0Qbyq9zTFTd-tUY6dOWWgUfXrH3g1E"
        }&libraries=places`;
      script.async = true;
      script.defer = true;
      script.onload = () => {
        initializeInteractiveMap();
      };
      script.onerror = () => {
        console.error("Failed to load Google Maps API");
      };
      document.head.appendChild(script);
    };

    const initializeInteractiveMap = () => {
      const mapElement = document.getElementById("interactive-map");
      if (!mapElement || !window.google) return;

      // Default center (Amman, Jordan)
      const defaultCenter = { lat: 31.9539, lng: 35.9106 };

      // Use existing coordinates or default
      const center =
        form.latitude && form.longitude
          ? { lat: parseFloat(form.latitude), lng: parseFloat(form.longitude) }
          : defaultCenter;

      const map = new window.google.maps.Map(mapElement, {
        zoom: 15,
        center: center,
        mapTypeId: "roadmap",
        styles: [
          {
            featureType: "poi",
            elementType: "labels",
            stylers: [{ visibility: "off" }],
          },
        ],
      });

      let marker = null;

      // Add initial marker if coordinates exist
      if (form.latitude && form.longitude) {
        marker = new window.google.maps.Marker({
          position: center,
          map: map,
          title: "Clinic Location",
          draggable: true,
        });
      }

      // Add click listener to place/update marker
      map.addListener("click", (event) => {
        const lat = event.latLng.lat();
        const lng = event.latLng.lng();

        // Update form state
        setForm((prev) => ({
          ...prev,
          latitude: lat.toString(),
          longitude: lng.toString(),
          map_coordinates: { lat: lat, lng: lng },
        }));

        // Update or create marker
        if (marker) {
          marker.setPosition(event.latLng);
        } else {
          marker = new window.google.maps.Marker({
            position: event.latLng,
            map: map,
            title: t("clinicSetup.location.markerTitle"),
            draggable: true,
          });
        }

        // Store marker reference globally
        window.interactiveMapMarker = marker;
      });

      // Add drag listener to marker
      if (marker) {
        marker.addListener("dragend", (event) => {
          const lat = event.latLng.lat();
          const lng = event.latLng.lng();

          setForm((prev) => ({
            ...prev,
            latitude: lat.toString(),
            longitude: lng.toString(),
            map_coordinates: { lat: lat, lng: lng },
          }));
        });

        // Store marker reference globally
        window.interactiveMapMarker = marker;
      }

      // Store map reference for cleanup
      window.interactiveMap = map;
    };

    // Load maps after a short delay to ensure DOM is ready
    const timer = setTimeout(loadGoogleMaps, 1000);

    return () => {
      clearTimeout(timer);
      if (window.interactiveMap) {
        window.interactiveMap = null;
      }
    };
  }, [form.latitude, form.longitude]);

  useEffect(() => {
    // Use centralized data if available
    if (clinicData?.countries) {
      setCountries(clinicData.countries);
      const options = clinicData.countries.map((country) => ({
        value: country.id,
        label: country.name_en,
      }));
      setCountryOptions(options);
    } else {
      // Fallback to API call if centralized data not available
      const fetchCountries = async () => {
        try {
          const response = await fetch(
            `${API_BASE_URL}/api/v1/locations/countries`
          );
          if (!response.ok) {
            throw new Error("Failed to fetch countries");
          }
          const result = await response.json();
          if (result.status === "success" && Array.isArray(result.data)) {
            setCountries(result.data);
            const options = result.data.map((country) => ({
              value: country.id,
              label: country.name_en,
            }));
            setCountryOptions(options);
          }
        } catch (err) {
          console.error("Error fetching countries:", err);
          setError(t("clinicSetup.errors.loadCountries"));
        }
      };
      fetchCountries();
    }
  }, [clinicData]);

  // Fetch cities when country changes - use centralized data
  useEffect(() => {
    if (!form.country) return;

    // Use centralized data if available
    if (clinicData?.countries) {
      const selectedCountry = clinicData.countries.find(
        (country) => country.id.toString() === form.country.toString()
      );
      if (selectedCountry && selectedCountry.cities) {
        const options = selectedCountry.cities.map((city) => ({
          value: city.id,
          label: city.name_en,
        }));
        setCityOptions(options);
        return;
      }
    }

    // Fallback to API call if centralized data not available
    const fetchCities = async () => {
      try {
        const response = await fetch(
          `${API_BASE_URL}/api/v1/locations/cities/country/${form.country}`
        );
        if (!response.ok) {
          throw new Error("Failed to fetch cities");
        }
        const result = await response.json();
        if (result.status === "success" && Array.isArray(result.data)) {
          const options = result.data.map((city) => ({
            value: city.id,
            label: city.name_en,
          }));
          setCityOptions(options);
        }
      } catch (err) {
        console.error("Error fetching cities:", err);
        setError(t("clinicSetup.errors.loadCities"));
      }
    };
    fetchCities();
  }, [form.country, clinicData]);

  const [clinicServices, setClinicServices] = useState([]);

  useEffect(() => {
    // Use centralized data if available
    if (clinicData?.clinic_service_list) {
      setClinicServices(clinicData.clinic_service_list);
    } else {
      // Fallback to API call if centralized data not available
      async function fetchClinicServices() {
        try {
          const response = await fetch(
            `${API_BASE_URL}/api/v1/clinic-service-list`
          );
          if (!response.ok) throw new Error("Failed to fetch clinic services");
          const result = await response.json();
          // Flatten nested array if needed
          const flatServices = Array.isArray(result.data)
            ? result.data.flat()
            : [];
          setClinicServices(flatServices);
        } catch (err) {
          console.error("Error fetching clinic services:", err);
        }
      }
      fetchClinicServices();
    }
  }, [clinicData]);

  // Refresh clinic data when success modal opens
  useEffect(() => {
    if (showClinicAdditionModal) {
      // Use refresh function to trigger update across all components
      // This will refetch clinic-setup-data through the useClinicData hook
      refresh();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showClinicAdditionModal]);

  async function updateClinic(clinicData) {
    const formdata = new FormData();
    formdata.append("name_en", clinicData.name_en);
    formdata.append("name_ar", clinicData.name_ar);
    formdata.append("registration_number", clinicData.registrationNumber);
    formdata.append(
      "establishment_date",
      clinicData.establishmentDate || clinicData.establishment_date
    );
    formdata.append("type", clinicData.type);
    formdata.append("vat_number", clinicData.vatNumber);
    formdata.append("currency", clinicData.currency);
    formdata.append(
      "invoice_starting_number",
      clinicData.invoiceStart || clinicData.invoice_starting_number
    );
    formdata.append("telephone", clinicData.telephone);
    formdata.append("mobile", clinicData.mobile);
    formdata.append("email", clinicData.email);
    formdata.append("website", clinicData.website);
    formdata.append("country", clinicData.country);
    formdata.append("city", clinicData.city);
    // Support new API fields
    formdata.append("country_id", clinicData.country);
    formdata.append("city_id", clinicData.city);
    formdata.append("street", clinicData.street);
    formdata.append("building_number", clinicData.buildingNumber);
    formdata.append("floor_number", clinicData.floorNumber);
    // Map coordinates - send lat and lng separately
    if (clinicData.latitude && clinicData.longitude) {
      formdata.append("latitude", parseFloat(clinicData.latitude));
      formdata.append("longitude", parseFloat(clinicData.longitude));
    }
    // Services offered
    (clinicData.servicesOffered || []).forEach((id) => {
      formdata.append("services_offered[]", id);
    });
    // Working hours
    const workingHoursArray = Object.entries(clinicData.workingHours || {})
      .filter(([day, value]) => value.enabled)
      .map(([day, value]) => ({ day, ...value }));
    workingHoursArray.forEach((obj, idx) => {
      formdata.append(`working_hours[${idx}][day]`, obj.day);
      formdata.append(`working_hours[${idx}][enabled]`, obj.enabled ? 1 : 0);
      formdata.append(`working_hours[${idx}][from]`, obj.from);
      formdata.append(`working_hours[${idx}][to]`, obj.to);
    });
    // Pet store flag
    formdata.append("is_pet_store", clinicData.is_pet_store ? "1" : "0");
    // Logo
    if (clinicData.logo) {
      formdata.append("logo", clinicData.logo);
    }
    // Laravel expects POST with _method=PUT for FormData
    formdata.append("_method", "PUT");
    // Add Authorization if needed
    const myHeaders = new Headers();
    myHeaders.append("Accept", "application/json");
    myHeaders.append("Authorization", `Bearer ${token}`);
    const response = await fetch(
      `${API_BASE_URL}/api/v1/clinics/${clinicIdValue}`,
      {
        method: "POST",
        headers: myHeaders,
        body: formdata,
      }
    );

    const responseData = await response.json();

    // Handle error responses
    if (!response.ok) {
      // Extract error message from response
      let errorMessage = t("clinicSetup.errors.updateClinic");

      if (responseData.message) {
        errorMessage = responseData.message;
      } else if (responseData.errors) {
        // Handle validation errors
        const errorMessages = Object.values(responseData.errors).flat();
        errorMessage = errorMessages.join(", ") || errorMessage;
      } else if (responseData.error) {
        errorMessage = responseData.error;
      }

      throw new Error(errorMessage);
    }

    return responseData;
  }


  const handleInputChange = (e) => {
    const { name, value, files } = e.target;
    if (name === "logo" && files && files[0]) {
      setForm((prev) => ({ ...prev, logo: files[0] }));
      setLogoPreview(URL.createObjectURL(files[0]));
    } else {
      setForm((prev) => ({ ...prev, [name]: files ? files[0] : value }));
    }
  };



  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitError("");


    // Validate all sections before submitting
    if (!validateAllSections()) {
      return;
    }


    try {
      setIsLoading(true);
      const response = await updateClinic(form);

      // Check if response indicates success
      // API typically returns { status: "success", data: {...} } or just { data: {...} }
      const isSuccess = response.status === "success" ||
        (response.data && !response.status) ||
        (response.status === 200);


      if (isSuccess) {
        const clinicId = response.data?.id || response.id;
        const isOwner = response.data?.is_owner || response.is_owner || true;


        // Update user data with clinic_id
        const userData = localStorage.getItem("user_data");
        if (userData && userData !== "undefined" && userData !== "null") {
          const userDataParsed = JSON.parse(userData);
          userDataParsed.clinic_id = clinicId;
          localStorage.setItem("user_data", JSON.stringify(userDataParsed));
        }

        // Store clinic object for reception page
        if (clinicId) {
          localStorage.setItem(
            "clinic",
            JSON.stringify({ is_owner: isOwner, clinic_id: clinicId })
          );
        }

        // Refresh clinic data
        refresh();

        // Show success modal
        setShowClinicAdditionModal(true);
      } else {
        // Handle API-level error messages
        const errorMsg = response.message ||
          response.error ||
          t("clinicSetup.errors.updateClinic");
        console.error("API returned non-success status:", response);
        throw new Error(errorMsg);
      }
    } catch (error) {
      const errorMessage = error?.message || t("clinicSetup.errors.updateClinic");
      console.error("Error updating clinic:", error);
      console.error("Error message:", errorMessage);
      setSubmitError(errorMessage);
      showErrorToast(errorMessage);
      setShowClinicAdditionModal(false);
    } finally {
      setIsLoading(false);
    }
  };
  const handleServiceChange = (serviceId) => (e) => {
    const { checked } = e.target;



    setForm((prev) => {
      let updated = [...(prev.servicesOffered || [])];
      if (checked) {
        if (!updated.includes(serviceId)) {
          updated.push(serviceId);
        }
      } else {
        updated = updated.filter((service) => service !== serviceId);
      }
      return { ...prev, servicesOffered: updated };
    });
  };
  const handleCancel = () => {
    navigate("/medical/dashboard");
  };

  // Clear pin functionality
  const handleClearPin = () => {
    setForm((prev) => ({
      ...prev,
      latitude: "",
      longitude: "",
      map_coordinates: {},
    }));

    // Clear marker from map if it exists
    if (window.interactiveMap && window.interactiveMapMarker) {
      window.interactiveMapMarker.setMap(null);
      window.interactiveMapMarker = null;
    }
  };

  // Map search functionality
  const handleMapSearch = async () => {
    if (!form.mapSearchQuery.trim()) return;

    try {
      // Use Google Geocoding API to get coordinates
      const response = await fetch(
        `https://maps.googleapis.com/maps/api/geocode/json?address=${encodeURIComponent(
          form.mapSearchQuery
        )}&key=${import.meta.env.VITE_GOOGLE_MAP_API_KEY ||
        "AIzaSyBFw0Qbyq9zTFTd-tUY6dOWWgUfXrH3g1E"
        }`
      );
      const data = await response.json();

      if (data.status === "OK" && data.results.length > 0) {
        const location = data.results[0].geometry.location;
        setForm((prev) => ({
          ...prev,
          latitude: location.lat.toString(),
          longitude: location.lng.toString(),
          map_coordinates: { lat: location.lat, lng: location.lng },
        }));

        // Update the interactive map if it exists
        if (window.interactiveMap) {
          const newCenter = { lat: location.lat, lng: location.lng };
          window.interactiveMap.setCenter(newCenter);
          window.interactiveMap.setZoom(15);

          // Remove existing marker if it exists
          if (window.interactiveMapMarker) {
            window.interactiveMapMarker.setMap(null);
          }

          // Create new marker at searched location
          const marker = new window.google.maps.Marker({
            position: newCenter,
            map: window.interactiveMap,
            title: t("clinicSetup.location.markerTitle"),
            draggable: true,
            animation: window.google.maps.Animation.DROP,
          });

          // Add drag listener to the new marker
          marker.addListener("dragend", (event) => {
            const lat = event.latLng.lat();
            const lng = event.latLng.lng();

            setForm((prev) => ({
              ...prev,
              latitude: lat.toString(),
              longitude: lng.toString(),
              map_coordinates: { lat: lat, lng: lng },
            }));
          });

          // Store marker reference globally
          window.interactiveMapMarker = marker;
        }
      } else {
        showErrorToast(t("clinicSetup.alerts.locationNotFound"));
      }
    } catch (error) {
      console.error("Error searching location:", error);
      showErrorToast(t("clinicSetup.alerts.locationSearchError"));
    }
  };

  // Get map source URL based on available data
  const getMapSource = () => {
    const apiKey = import.meta.env.VITE_GOOGLE_MAP_API_KEY;
    // Priority 1: Use coordinates if available
    if (form.latitude && form.longitude) {
      return `https://www.google.com/maps/embed/v1/view?key=${apiKey}&center=${form.latitude},${form.longitude}&zoom=15`;
    }

    // Priority 2: Use address components
    if (form.country && form.city && form.buildingNumber) {
      const address =
        form.street || `${form.buildingNumber}, ${form.city}, ${form.country}`;
      return `https://www.google.com/maps/embed/v1/place?key=${apiKey}&q=${encodeURIComponent(
        address
      )}`;
    }

    // Priority 3: Use search query if available
    if (form.mapSearchQuery.trim()) {
      return `https://www.google.com/maps/embed/v1/place?key=${apiKey}&q=${encodeURIComponent(
        form.mapSearchQuery
      )}`;
    }

    return null;
  };

  // Show loading state while fetching clinic data
  if (isLoading) {
    return (
      <div className="clinicsetup-page-wrapper">
        <Navbar />
        <div className="clinicsetup-container">
          <div
            style={{
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              height: "400px",
              flexDirection: "column",
              gap: "16px",
            }}
          >
            <div
              style={{
                width: "40px",
                height: "40px",
                border: "4px solid #f3f3f3",
                borderTop: "4px solid #3498db",
                borderRadius: "50%",
                animation: "spin 1s linear infinite",
              }}
            ></div>
            <p style={{ color: "#666", fontSize: "16px" }}>
              {t("clinicSetup.basicInfo.loadingPrimary")}
            </p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <ResolutionGuard minWidth={768}>
      <div
        className="clinicsetup-page-wrapper clinicinfo-member-wrapper"
        dir={i18n.dir()}
        lang={i18n.language}
      >
        <Navbar />
        <div className="clinicinfo-member-wrapper" style={{
          background: "#fff",
          borderRadius: "0",
          margin: "0",
          minHeight: "calc(100vh - 80px)",
          boxShadow: "none",
          padding: "0",
          display: "flex",
          flexDirection: "column",
          height: "calc(100vh - 80px)",
          overflow: "hidden"
        }}>
          {/* Modern Step Navigation */}
          <div
            ref={stepNavigationRef}
            style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              gap: "8px",
              padding: "24px 32px",
              paddingBottom: "24px",
              marginBottom: "0",
              borderBottom: "2px solid #e5e7eb",
              flexShrink: 0,
              width: "100%",
              maxWidth: "1040px",
              marginInline: "auto"
            }}
          >
            {stepLabels.map((label, idx) => (
              <React.Fragment key={STEP_KEYS[idx]}>
                <div
                  onClick={() => setCurrentSection(idx)}
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: "8px",
                    cursor: "pointer",
                    userSelect: "none",
                    padding: "8px 16px",
                    borderRadius: "8px",
                    transition: "all 0.2s ease",
                    background: currentSection === idx ? "rgba(0,212,179,0.12)" : "transparent"
                  }}
                  onMouseEnter={(e) => {
                    if (currentSection !== idx) {
                      e.currentTarget.style.background = "#f9fafb";
                    }
                  }}
                  onMouseLeave={(e) => {
                    if (currentSection !== idx) {
                      e.currentTarget.style.background = "transparent";
                    }
                  }}
                >
                  <div style={{
                    width: "32px",
                    height: "32px",
                    borderRadius: "50%",
                    background: currentSection === idx ? "#00D4B3" : "#e5e7eb",
                    color: currentSection === idx ? "#0b2e2a" : "#6b7280",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    fontSize: "14px",
                    fontWeight: "600",
                    transition: "all 0.2s ease"
                  }}>
                    {idx + 1}
                  </div>
                  <span style={{
                    fontWeight: currentSection === idx ? "600" : "500",
                    color: currentSection === idx ? "#0f766e" : "#6b7280",
                    fontSize: "14px",
                    transition: "all 0.2s ease"
                  }}>
                    {label}
                  </span>
                </div>
                {idx < LAST_SECTION_INDEX && (
                  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" style={{
                    color: "#d1d5db",
                    flexShrink: 0,
                    transform: i18n.dir() === "rtl" ? "scaleX(-1)" : "none"
                  }}>
                    <path d="M9 18l6-6-6-6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                  </svg>
                )}
              </React.Fragment>
            ))}
          </div>

          {/* Form Content - Scrollable area */}
          <div
            ref={formScrollContainerRef}
            style={{
              flex: 1,
              overflowY: "auto",
              overflowX: "hidden",
              padding: "0 32px 32px 32px",
              display: "flex",
              justifyContent: "center"
            }}
          >
            <form
              onSubmit={handleSubmit}
              style={{ height: "100%", width: "100%", maxWidth: "960px" }}
            >
              {currentSection === 0 && (
                <BasicInfoSection
                  form={form}
                  setForm={setForm}
                  errors={errors}
                  fileInputRef={fileInputRef}
                  dateInputRef={dateInputRef}
                  logoPreview={logoPreview}
                  handleInputChange={handleInputChange}
                  clinicTypeOptions={clinicTypeOptions}
                  isLoading={isLoading}
                />
              )}
              {currentSection === 1 && (
                <BusinessDetailsSection
                  form={form}
                  errors={errors}
                  setForm={setForm}
                  handleInputChange={handleInputChange}
                />
              )}

              {currentSection === 2 && (
                <LocationSection
                  form={form}
                  errors={errors}
                  countryOptions={countryOptions}
                  cityOptions={cityOptions}
                  setForm={setForm}
                  handleInputChange={handleInputChange}
                  handleMapSearch={handleMapSearch}
                  handleClearPin={handleClearPin}
                />
              )}
              {currentSection === 3 && (
                <ServicesSection
                  clinicServices={clinicServices}
                  form={form}
                  handleServiceChange={handleServiceChange}
                  setForm={setForm}
                  errors={errors}
                />
              )}

              {submitError && (
                <div
                  style={{
                    color: "#e53935",
                    marginBottom: "16px",
                    textAlign: "center",
                  }}
                >
                  {submitError}
                </div>
              )}
              {/* Action Buttons */}
              <div
                style={{
                  display: "flex",
                  width: "100%",
                  justifyContent: "center",
                  marginTop: "24px",
                  paddingTop: "24px",
                  borderTop: "2px solid #e5e7eb",
                  flexShrink: 0
                }}
              >
                <button
                  className="clinicsetup-btn clinicsetup-cancel-btn"
                  style={{ marginRight: "10px" }}
                  type="button"
                  onClick={() => {
                    if (currentSection === 0) {
                      const targetClinicId = clinicIdValue || clinicData?.clinic?.id;
                      if (targetClinicId) {
                        navigate(`/medical/clinic/${targetClinicId}/setup`);
                      } else {
                        navigate("/medical/clinic/setup");
                      }
                    } else {
                      setCurrentSection(currentSection - 1);
                    }
                  }}
                  disabled={currentSection === 0}
                >
                  {t("clinicSetup.buttons.back")}
                </button>
                {currentSection < LAST_SECTION_INDEX ? (
                  <button
                    className="clinicsetup-btn clinicsetup-next-btn"
                    type="button"
                    onClick={() => {
                      if (validateCurrentSection()) {
                        setCurrentSection(currentSection + 1);
                      }
                    }}
                  >
                    {t("clinicSetup.buttons.next")}
                  </button>
                ) : (
                  <button
                    className="clinicsetup-next-btn"
                    type="button"
                    onClick={handleSubmit}
                    disabled={isLoading}
                  >
                    {isLoading ? t("clinicSetup.buttons.saving") : t("clinicSetup.buttons.save")}
                  </button>
                )}
                {currentSection !== 3 && (
                  <button
                    className="clinicsetup-save-btn"
                    onClick={handleSubmit}
                    disabled={isLoading}
                  >
                    {isLoading ? t("clinicSetup.buttons.saving") : t("clinicSetup.buttons.save")}
                  </button>
                )}
              </div>
            </form>
          </div>
          <ClinicAdditionModal
            open={showClinicAdditionModal}
            onClose={() => {
              setShowClinicAdditionModal(false);
              resetClinicData();
            }}
            isUpdate={true}
          />
        </div>
      </div>
    </ResolutionGuard>
  );
};

export default EditClinic;
